import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut as fbSignOut,
  sendEmailVerification as fbSendEmailVerification,
  sendPasswordResetEmail,
  confirmPasswordReset as fbConfirmPasswordReset,
  updatePassword as fbUpdatePassword,
  deleteUser,
  reauthenticateWithCredential,
  EmailAuthProvider,
  onAuthStateChanged,
  type User,
} from 'firebase/auth';
import { auth } from '../lib/firebase';

interface AuthContextValue {
  user: User | null;
  /** True until the first onAuthStateChanged callback resolves. */
  loading: boolean;
  /** Convenience flags derived from `user`. */
  isAuthenticated: boolean;
  emailVerified: boolean;

  signUp: (email: string, password: string) => Promise<User>;
  signIn: (email: string, password: string) => Promise<User>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (oobCode: string, newPassword: string) => Promise<void>;
  sendEmailVerification: () => Promise<void>;
  reloadUser: () => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  /** Re-authenticate the current user with their password (for sensitive ops). */
  reauthenticate: (password: string) => Promise<void>;
  deleteAccount: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

/** Where Firebase email action links (verify / reset) should land. */
const actionCodeSettings = {
  url: `${window.location.origin}/login`,
  handleCodeInApp: false,
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Persist + react to auth state across reloads and tabs.
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (next) => {
      setUser(next);
      setLoading(false);
    });
    return unsubscribe;
  }, []);

  const signUp = useCallback(async (email: string, password: string) => {
    const cred = await createUserWithEmailAndPassword(auth, email, password);
    await fbSendEmailVerification(cred.user, actionCodeSettings);
    return cred.user;
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const cred = await signInWithEmailAndPassword(auth, email, password);
    return cred.user;
  }, []);

  const signOut = useCallback(() => fbSignOut(auth), []);

  const resetPassword = useCallback(
    (email: string) => sendPasswordResetEmail(auth, email, actionCodeSettings),
    [],
  );

  const confirmPasswordReset = useCallback(
    (oobCode: string, newPassword: string) => fbConfirmPasswordReset(auth, oobCode, newPassword),
    [],
  );

  const sendEmailVerification = useCallback(async () => {
    if (!auth.currentUser) throw new Error('You must be signed in to verify your email.');
    await fbSendEmailVerification(auth.currentUser, actionCodeSettings);
  }, []);

  const reloadUser = useCallback(async () => {
    if (!auth.currentUser) return;
    await auth.currentUser.reload();
    // reload() mutates the existing object; clone the ref so React re-renders.
    setUser(auth.currentUser ? Object.assign(Object.create(Object.getPrototypeOf(auth.currentUser)), auth.currentUser) : null);
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!auth.currentUser) throw new Error('You must be signed in.');
    await fbUpdatePassword(auth.currentUser, newPassword);
  }, []);

  const reauthenticate = useCallback(async (password: string) => {
    if (!auth.currentUser?.email) throw new Error('No authenticated user to re-authenticate.');
    const credential = EmailAuthProvider.credential(auth.currentUser.email, password);
    await reauthenticateWithCredential(auth.currentUser, credential);
  }, []);

  const deleteAccount = useCallback(async () => {
    if (!auth.currentUser) throw new Error('You must be signed in.');
    await deleteUser(auth.currentUser);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      isAuthenticated: !!user,
      emailVerified: !!user?.emailVerified,
      signUp,
      signIn,
      signOut,
      resetPassword,
      confirmPasswordReset,
      sendEmailVerification,
      reloadUser,
      updatePassword,
      reauthenticate,
      deleteAccount,
    }),
    [
      user,
      loading,
      signUp,
      signIn,
      signOut,
      resetPassword,
      confirmPasswordReset,
      sendEmailVerification,
      reloadUser,
      updatePassword,
      reauthenticate,
      deleteAccount,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}
